import React, {
  useEffect, Suspense, useState, useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from '@common/ui/general/Toast';

import {
  verifyQuestions,
  getInitialResources,
  setIsSignupSidebarOpen,
} from '@alumni_v2/stores/reviews/actions';
import {
  getAuthData, removeAuthData, isUserLoggedIn,
  authTrigger,
} from '@alumni_v2/utils/helpers';
import { Toast, Modal } from '@common/ui/general';
import AlumniWrapper from '@alumni_v2/ui/AlumniWrapper';
import Filters from '@alumni_v2/ui/Filters';
import FilterRecommendationButton
  from '@alumni_v2/ui/FilterRecommendationButton';
import FilterTags from '@alumni_v2/ui/FilterTags';
import MissingDetails from '@alumni_v2/ui/MissingDetails';
import QnA from '@alumni_v2/ui/QnA';
import SearchBar from '@alumni_v2/ui/SearchBar';
import Review from '@alumni_v2/ui/Review';
import SuccessModal from '@alumni_v2/ui/SuccessModal';
import TopBanner from '@alumni_v2/ui/TopBanner';
import ShareProfile from '@alumni_v2/ui/ShareProfile';
import { ExitIntent } from '@vectord/fp-exit-intent';
import RcbSuccess from '@alumni_v2/ui/RcbSuccess';
import { requestACS, getACSStatus } from '@alumni_v2/api/acsConnect';
import { AcsSessionRequest } from '@vectord/acs-session-request';
import { gtmEventHandler } from '@common/utils/gtm';
import { useMediaQuery } from '@common/hooks';
import {
  personalisedModalSelector,
  profleSidebaSelector,
  signupSidebarSelector,
} from '@alumni_v2/selectors/reviews';
import styles from '../../main.module.scss';


function ReviewsPage() {
  const { mobile } = useMediaQuery();
  const dispatch = useDispatch();
  const { showExpertAcs } = window.__INITIAL_STATE__;
  const [showExitIntent, setShowExitIntent] = useState(true);
  const [showRcbSuccess, setShowRcbSuccess] = useState(false);
  const [acsStatus, setAcsStatus] = useState(null);
  const [isAcsRequested, setIsAcsRequested] = useState(null);
  const {
    isPersonalisedModalOpen,
  } = useSelector(personalisedModalSelector) || false;
  const {
    isProfileSidebarModalOpen,
  } = useSelector(profleSidebaSelector) || false;
  const {
    isSignupSideBarOpen,
  } = useSelector(signupSidebarSelector) || false;

  const fetchAcsStatus = useCallback(async () => {
    const status = await getACSStatus();
    setAcsStatus(status.registered);
  }, []);

  const isAcsRequestedBeforeSignIn = useCallback(() => {
    const acsRequested = sessionStorage.getItem('requested-acs') === 'true';
    setIsAcsRequested(acsRequested);
  }, []);


  const trackSessionReqest = useCallback((success) => {
    gtmEventHandler(
      'cta',
      `acs_review_${showExpertAcs ? 'Expert' : 'Alumni'}_exit_intent`,
      'click',
      'alumni_directory',
      {
        success_rcb: success,
      },
    );
  }, [showExpertAcs]);

  const signupSideBarListener = useCallback(() => {
    const parentContainer = document.getElementById('auth-modal');
    const closeButtonElement = parentContainer?.querySelector(
      '[data-action="modal-close"]',
    );
    const loginButtonEle = document.querySelector(
      '[data-name="success-stories-register"]',
    );

    loginButtonEle?.addEventListener('click', () => {
      dispatch(setIsSignupSidebarOpen(true));
    });

    closeButtonElement?.addEventListener('click', () => {
      dispatch(setIsSignupSidebarOpen(false));
      if (sessionStorage.getItem('requested-acs')) {
        sessionStorage.removeItem('requested-acs');
      }
    });
  }, [dispatch]);

  const acsRequestHandler = useCallback(async () => {
    try {
      const result = await requestACS();
      if (result.success) {
        setShowRcbSuccess(true);
        trackSessionReqest(true);
      } else {
        trackSessionReqest(false);
        toast.show({
          message: 'ACS request failed. Please try again later.',
          type: 'error',
          position: 'top-right',
          duration: 5000,
        });
      }
    } catch (error) {
      trackSessionReqest(false);
      toast.show({
        message: `An error occurred during ACS request: ${error}`,
        type: 'error',
        position: 'top-right',
        duration: 5000,
      });
    } finally {
      setShowExitIntent(false);
    }
  }, [trackSessionReqest]);

  const requestSessionHandler = useCallback(async () => {
    if (!isUserLoggedIn()) {
      toast.show({
        message: 'Please login or sign up before!',
        type: 'error',
        position: 'top-right',
        duration: 5000,
      });
      authTrigger();
      setShowExitIntent(false);
      sessionStorage.setItem('requested-acs', true);
      trackSessionReqest(false);
    } else {
      await acsRequestHandler();
    }
  }, [acsRequestHandler, trackSessionReqest, setShowExitIntent]);

  useEffect(() => {
    signupSideBarListener();
    if (isUserLoggedIn() && getAuthData()) {
      dispatch(verifyQuestions(getAuthData()));
      removeAuthData();
    }

    if (isUserLoggedIn()) {
      fetchAcsStatus();
      isAcsRequestedBeforeSignIn();
      dispatch(getInitialResources());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUserLoggedIn() && isAcsRequested && !acsStatus
    && acsStatus !== null) {
      acsRequestHandler();
    }

    if (!isUserLoggedIn() && sessionStorage.getItem('requested-acs')) {
      sessionStorage.removeItem('requested-acs');
    }
  }, [isAcsRequested, acsStatus, acsRequestHandler]);

  return (
    <>
      <AlumniWrapper
        Filter={Filters}
        SearchBar={SearchBar}
        Banner={TopBanner}
        FilterTags={FilterTags}
        ShareProfile={ShareProfile}
        FilterRecommendationButton={FilterRecommendationButton}
      >
        <Suspense fallback={<div>Loading...</div>}>
          <MissingDetails />
          <QnA />
          <SuccessModal />
        </Suspense>
        <Review />
      </AlumniWrapper>
      {showExitIntent && !mobile && !isPersonalisedModalOpen
      && !acsStatus && !isProfileSidebarModalOpen
      && !isSignupSideBarOpen && (
        <div className="scaler">
          <ExitIntent
            exitIntentId="review-exit-intent-modal"
            showCloseBtn
            mobileDelay={45000}
            product="free_product"
            subproduct="review_page"
            wrapperClassName="review-page__exit-intent-wrapper"
          >
            <Toast />
            <AcsSessionRequest
              wrapperClassName="vd-bg-surface-neutral-base"
              title={
                showExpertAcs
                  ? 'Ask Successful Learners for FREE!'
                  : 'Ask Scaler Alumni for FREE!'
              }
              buttonText={
                showExpertAcs
                  ? 'Request an Expert Session'
                  : 'Request an Alumni Session'
              }
              footerText={
                showExpertAcs
                  ? 'Users connected with Experts'
                  : 'Users connected with Alumni'
              }
              onSubmitHandler={requestSessionHandler}
            />
          </ExitIntent>
        </div>
      )}
      <Modal
        isOpen={showRcbSuccess}
        onClose={() => setShowRcbSuccess(false)}
        className={styles.acsSuccessModal}
        withoutHeader
        canClose
        hasCloseButton
      >
        <RcbSuccess checkLaterHandler={setShowRcbSuccess} />
      </Modal>
    </>
  );
}

export default ReviewsPage;
